import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { ContentWrapper } from '../../shared/ContentWrapper/ContentWrapper';
import { getToken } from '../../services/auth';
import {
  getDishAttributes,
  createDishAttribute,
  updateDishAttribute,
  deleteDishAttribute,
  getDishAttributeNames,
  createDishAttributeName,
} from '../../api/dishAttributes';

const DishAttributes = () => {
  const storeId = useSelector((state) => state.store.id);
  const [attributeGroups, setAttributeGroups] = useState([]);
  const [attributeNames, setAttributeNames] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);

  // Form State (Abstracted from technical tables)
  const [editingGroupId, setEditingGroupId] = useState(null); // null if new
  const [categoryId, setCategoryId] = useState(''); // 'new' if creating new
  const [newCategoryName, setNewCategoryName] = useState('');
  
  // The actual options for the "Name" attribute
  const [options, setOptions] = useState([]); 
  const [currentOptionText, setCurrentOptionText] = useState('');

  // Toggles for extra attributes
  const [allowsPrice, setAllowsPrice] = useState(false);
  const [allowsModifier, setAllowsModifier] = useState(false);

  // Store the actual backend IDs so we can update/delete them
  const [nameAttrId, setNameAttrId] = useState(null);
  const [priceAttrId, setPriceAttrId] = useState(null);
  const [modifierAttrId, setModifierAttrId] = useState(null);

  useEffect(() => {
    if (storeId) {
      fetchData();
    }
  }, [storeId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const namesRes = await getDishAttributeNames(token);
      setAttributeNames(namesRes.results || namesRes);

      const attrsRes = await getDishAttributes({ storeId, token });
      const rawAttrs = attrsRes.results || attrsRes;

      // Group the raw attributes by storeAttributeName
      const groupsMap = {};
      rawAttrs.forEach((attr) => {
        const catId = (attr.storeAttributeName && attr.storeAttributeName.id) || attr.store_attribute_name_id;
        const catName = attr.storeAttributeName && attr.storeAttributeName.name;

        if (!groupsMap[catId]) {
          groupsMap[catId] = {
            categoryId: catId,
            categoryName: catName,
            nameAttr: null,
            priceAttr: null,
            modifierAttr: null,
          };
        }

        if (attr.name.toLowerCase() === 'name') {
          groupsMap[catId].nameAttr = attr;
        } else if (attr.name.toLowerCase() === 'price') {
          groupsMap[catId].priceAttr = attr;
        } else if (attr.name.toLowerCase() === 'modifier') {
          groupsMap[catId].modifierAttr = attr;
        }
      });

      setAttributeGroups(Object.values(groupsMap));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (group = null) => {
    if (group) {
      setEditingGroupId(group.categoryId);
      setCategoryId(group.categoryId);
      setNewCategoryName('');

      // Extract options from the "Name" attribute's JSON values
      if (group.nameAttr && group.nameAttr.values && group.nameAttr.values.value) {
        setOptions(group.nameAttr.values.value.map(v => v.name));
      } else {
        setOptions([]);
      }
      setNameAttrId(group.nameAttr ? group.nameAttr.id : null);

      // Extract toggles
      setAllowsPrice(!!group.priceAttr);
      setPriceAttrId(group.priceAttr ? group.priceAttr.id : null);

      setAllowsModifier(!!group.modifierAttr);
      setModifierAttrId(group.modifierAttr ? group.modifierAttr.id : null);
    } else {
      // Reset for New
      setEditingGroupId(null);
      setCategoryId('');
      setNewCategoryName('');
      setOptions([]);
      setAllowsPrice(false);
      setAllowsModifier(false);
      setNameAttrId(null);
      setPriceAttrId(null);
      setModifierAttrId(null);
    }
    setCurrentOptionText('');
    setModalOpen(true);
  };

  const handleAddOption = (e) => {
    if (e.key === 'Enter' && currentOptionText.trim() !== '') {
      e.preventDefault();
      if (!options.includes(currentOptionText.trim())) {
        setOptions([...options, currentOptionText.trim()]);
      }
      setCurrentOptionText('');
    }
  };

  const handleDeleteOption = (optionToDelete) => {
    setOptions(options.filter((opt) => opt !== optionToDelete));
  };

  const handleSave = async () => {
    try {
      const token = getToken();
      let finalCategoryId = categoryId;

      // 1. Create category if 'new'
      if (categoryId === 'new') {
        const res = await createDishAttributeName({ token, name: newCategoryName });
        finalCategoryId = res.id;
      }

      // 2. Handle the "Name" attribute (where choices live)
      const formattedValues = { value: options.map(opt => ({ name: opt })) };
      const namePayload = {
        store_attribute_name: finalCategoryId,
        store_branch: storeId,
        name: 'Name',
        type: 'string',
        values: formattedValues,
        price_change: false,
      };

      if (nameAttrId) {
        await updateDishAttribute({ storeId, id: nameAttrId, payload: namePayload, token });
      } else {
        await createDishAttribute({ storeId, payload: namePayload, token });
      }

      // 3. Handle the "price" attribute
      if (allowsPrice) {
        const pricePayload = {
          store_attribute_name: finalCategoryId,
          store_branch: storeId,
          name: 'price',
          type: 'number',
          values: {},
          price_change: true,
        };
        if (priceAttrId) {
          await updateDishAttribute({ storeId, id: priceAttrId, payload: pricePayload, token });
        } else {
          await createDishAttribute({ storeId, payload: pricePayload, token });
        }
      } else if (priceAttrId) {
        // User unchecked price, delete the technical attribute
        await deleteDishAttribute({ storeId, id: priceAttrId, token });
      }

      // 4. Handle the "modifier" attribute
      if (allowsModifier) {
        const modPayload = {
          store_attribute_name: finalCategoryId,
          store_branch: storeId,
          name: 'modifier',
          type: 'string',
          values: {},
          price_change: false,
        };
        if (modifierAttrId) {
          await updateDishAttribute({ storeId, id: modifierAttrId, payload: modPayload, token });
        } else {
          await createDishAttribute({ storeId, payload: modPayload, token });
        }
      } else if (modifierAttrId) {
        await deleteDishAttribute({ storeId, id: modifierAttrId, token });
      }

      setModalOpen(false);
      fetchData();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteGroup = async (group) => {
    if (window.confirm(`Are you sure you want to delete all attributes for ${group.categoryName}?`)) {
      try {
        const token = getToken();
        if (group.nameAttr) await deleteDishAttribute({ storeId, id: group.nameAttr.id, token });
        if (group.priceAttr) await deleteDishAttribute({ storeId, id: group.priceAttr.id, token });
        if (group.modifierAttr) await deleteDishAttribute({ storeId, id: group.modifierAttr.id, token });
        fetchData();
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <ContentWrapper>
      <Container maxWidth="lg" style={{ marginTop: '40px' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">Menu Options (Dish Attributes)</Typography>
          <Button variant="contained" color="primary" onClick={() => handleOpenModal()}>
            Add Menu Option
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Category (Group)</TableCell>
                <TableCell>Options (Choices)</TableCell>
                <TableCell>Allows Price?</TableCell>
                <TableCell>Allows Modifier?</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attributeGroups.map((group) => {
                const choices = (group.nameAttr && group.nameAttr.values && group.nameAttr.values.value && group.nameAttr.values.value.map(v => v.name)) || [];
                return (
                  <TableRow key={group.categoryId}>
                    <TableCell><strong>{group.categoryName}</strong></TableCell>
                    <TableCell>
                      {choices.map(c => (
                        <Chip key={c} label={c} size="small" style={{ margin: '2px' }} />
                      ))}
                    </TableCell>
                    <TableCell>{group.priceAttr ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{group.modifierAttr ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleOpenModal(group)}>
                        <EditIcon color="primary" />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteGroup(group)}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGroupId ? 'Edit Menu Option' : 'Add Menu Option'}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap="16px">
            <TextField
              select
              label="Category (Group)"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              fullWidth
              variant="outlined"
              style={{ marginBottom: '16px' }}
              disabled={!!editingGroupId} // Cannot change category if editing
            >
              <MenuItem value="new">+ Create New Category</MenuItem>
              {attributeNames.map((name) => (
                <MenuItem key={name.id} value={name.id}>
                  {name.name}
                </MenuItem>
              ))}
            </TextField>

            {categoryId === 'new' && (
              <TextField
                label="New Category Name (e.g. Add Protein)"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                fullWidth
                variant="outlined"
                style={{ marginBottom: '16px' }}
              />
            )}

            <Box mb={2}>
              <Typography variant="subtitle2" gutterBottom>
                Choices (e.g. Chicken, Beef)
              </Typography>
              <Box display="flex" flexWrap="wrap" gap="8px" mb={1} style={{ gap: '8px' }}>
                {options.map((opt) => (
                  <Chip
                    key={opt}
                    label={opt}
                    onDelete={() => handleDeleteOption(opt)}
                    color="primary"
                    style={{ margin: '4px' }}
                  />
                ))}
              </Box>
              <TextField
                label="Type a choice and press Enter"
                value={currentOptionText}
                onChange={(e) => setCurrentOptionText(e.target.value)}
                onKeyDown={handleAddOption}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={allowsPrice}
                  onChange={(e) => setAllowsPrice(e.target.checked)}
                  color="primary"
                />
              }
              label="Each choice can have an additional price (+$)"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={allowsModifier}
                  onChange={(e) => setAllowsModifier(e.target.checked)}
                  color="primary"
                />
              }
              label="Allow Modifiers (e.g. half/half, lite)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </ContentWrapper>
  );
};

export default DishAttributes;
